import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip } from 'lucide-react';

import { useActiveClinic } from '@/lib/activeClinic';
import { callFunction } from '@/lib/api';
import { MEDBIN_FILE_ACCEPT, checkMedbinFile, type MedbinLimitCode } from '@/lib/medbinLimits';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ResponsiveTable } from '@/components/ui/Table';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

type Kind = 'XBIN01' | 'XBIN02';

const PARENTS = [
  { id: 'XDIS91', label: 'XDIS91 — korrespondance', kind: 'XBIN01' },
  { id: 'XREF01', label: 'XREF01 — sygehushenvisning', kind: 'XBIN02' },
  { id: 'XREF02', label: 'XREF02 — billeddiagnostisk henvisning', kind: 'XBIN02' },
  { id: 'XREF06', label: 'XREF06 — speciallægehenvisning', kind: 'XBIN02' },
  { id: 'REF12', label: 'REF12 — Øfeldthenvisning', kind: 'XBIN02' },
  { id: 'XREF15', label: 'XREF15 — kommunehenvisning', kind: 'XBIN02' },
  { id: 'XREF22', label: 'XREF22 — kommunal akutfunktion', kind: 'XBIN02' },
  { id: 'REF07', label: 'REF07', kind: null },
  { id: 'REF08', label: 'REF08', kind: null },
  { id: 'REF10', label: 'REF10', kind: null },
] as const;

type ParentId = (typeof PARENTS)[number]['id'];

function parentById(id: ParentId): (typeof PARENTS)[number] {
  const parent = PARENTS.find((item) => item.id === id);
  if (!parent) {
    throw new Error(`Unknown parent ${id}`);
  }
  return parent;
}

type Check = { id: string; pass: boolean; detail: string };

type Preview = {
  consumerName: string;
  ok: boolean;
  xml: string;
  parentXml: string | null;
  parentReference: string | null;
  checks: Check[];
};

function limitMessage(code: MedbinLimitCode): string {
  switch (code) {
    case 'blocked-type':
      return 'dll og docx kan ikke vedhæftes.';
    case 'unknown-type':
      return 'Filtypen er ikke tilladt.';
    case 'file-too-large':
      return 'Én fil må højst være 100 MB.';
    case 'too-many-files':
      return 'Der må højst være 10 filer.';
    case 'total-too-large':
      return 'Filerne er tilsammen over 100 MB.';
  }
}

function downloadFile(name: string, contents: string) {
  const blob = new Blob([contents], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

type InboxRow = {
  fileName: string;
  status: 'forælder' | 'bilag' | 'afventer' | 'afvist' | 'kvittering';
  detail: string;
};

function localName(el: Element | null): string {
  return el?.localName ?? '';
}

function textOf(parent: ParentNode, name: string): string {
  const nodes = parent.querySelectorAll('*');
  for (const node of nodes) {
    if (localName(node) === name && node.textContent) return node.textContent.trim();
  }
  return '';
}

function classifyImport(fileName: string, xml: string, knownParents: Set<string>): InboxRow {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  const root = doc.documentElement;
  if (localName(root) !== 'Emessage') {
    return { fileName, status: 'afvist', detail: 'Filen er ikke et MedCom-brev og er ikke indlæst.' };
  }
  const hasBinary = textOf(root, 'Object_Base64Encoded').length > 0;
  if (!hasBinary) {
    const reference = textOf(root, 'ObjectIdentifier').replace(/-/g, '');
    if (reference) knownParents.add(reference);
    return {
      fileName,
      status: 'forælder',
      detail: 'Forældrebrevet er indlæst. Bilaget mangler stadig. Brevet er ikke afvist.',
    };
  }
  const declared = Number(textOf(root, 'OriginalObjectSize'));
  const payload = textOf(root, 'Object_Base64Encoded').replace(/\s/g, '');
  const pad = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  const actual = Math.floor((payload.length * 3) / 4) - pad;
  if (!Number.isFinite(declared) || declared !== actual) {
    return {
      fileName,
      status: 'afvist',
      detail: 'Bilaget er ikke indlæst. Størrelsen matcher ikke. Negativ kvittering gælder kun bilaget.',
    };
  }
  const objectId = textOf(root, 'ObjectIdentifier');
  const parentKey = objectId.replace(/-/g, '');
  if (!knownParents.has(parentKey)) {
    return { fileName, status: 'afventer', detail: 'Bilaget er holdt. Forældrebrevet er ikke modtaget.' };
  }
  return { fileName, status: 'bilag', detail: 'Bilaget er lagt på forældrebrevet.' };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result ?? '');
      const comma = value.indexOf(',');
      resolve(comma >= 0 ? value.slice(comma + 1) : value);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Filen kunne ikke læses'));
    reader.readAsDataURL(file);
  });
}

export function MedbinAttachment(_props: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const connection = useClinicCommunicateConnection(activeClinicId);
  const [parentId, setParentId] = useState<ParentId>('XDIS91');
  const parent = parentById(parentId);
  const kind: Kind | null = parent.kind;
  const [senderName, setSenderName] = useState('Clinio Test Klinik');
  const [receiverEan, setReceiverEan] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [patientCpr, setPatientCpr] = useState('');
  const [patientGiven, setPatientGiven] = useState('');
  const [patientSurname, setPatientSurname] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inbox, setInbox] = useState<InboxRow[]>([]);
  const [knownParents] = useState(() => new Set<string>());

  const senderEan = connection.data?.ean ?? '';

  const onCheck = async () => {
    setError(null);
    setPreview(null);
    if (!activeClinicId) {
      setError('Vælg en klinik.');
      return;
    }
    const file = files[0];
    if (!kind) {
      setError('Der kan ikke vedhæftes bilag til denne type.');
      return;
    }
    if (!file) {
      setError('Vælg en fil.');
      return;
    }
    if (!senderEan) {
      setError('Klinikken har ikke et EAN på Communicate-forbindelsen endnu.');
      return;
    }
    setBusy(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const result = await callFunction<Record<string, unknown>, Preview>('communicate-medbin', {
        clinicId: activeClinicId,
        kind,
        sender: { ean: senderEan, name: senderName },
        receiver: { ean: receiverEan, name: receiverName },
        patientCpr,
        patientGivenName: patientGiven,
        patientSurname,
        fileName: file.name,
        fileBase64,
        existingFileCount: 0,
        existingTotalBytes: 0,
      });
      setPreview(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke tjekke bilaget.');
    } finally {
      setBusy(false);
    }
  };

  const checkButton = (
    <Button type="button" onClick={() => void onCheck()} disabled={busy || !kind}>
      <Paperclip className="mr-s2 h-4 w-4" />
      {busy ? t('medcomSection.attachment_checking') : t('medcomSection.attachment_check')}
    </Button>
  );

  return (
    <div>
      <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center min-h-10">
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('medcomSection.title')}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">{checkButton}</div>
      </div>

      <div className="mt-s5 space-y-s5">
        <Card>
        <div className="grid max-w-3xl gap-s4">
          <label className={cn(TYPE.label, 'block text-ds-text')}>
            <span className="mb-s1 block">Forældretype</span>
            <Select
              value={parentId}
              onChange={(event) => {
                setParentId(event.target.value as ParentId);
                setFiles([]);
                setPreview(null);
                setError(null);
              }}
            >
              {PARENTS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </Select>
          </label>
          {kind ? (
            <p className="text-sm text-ds-muted">Bilag: {kind}. Brevet er ikke udfyldt. Denne skærm er kun bilaget.</p>
          ) : (
            <p className="text-sm text-ds-red-ink">Der kan ikke vedhæftes bilag til {parentId}.</p>
          )}
          <p className="text-sm text-ds-muted">Afsender EAN: {senderEan || 'mangler på klinikken'}</p>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Afsender navn</span>
            <Input value={senderName} onChange={(event) => setSenderName(event.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Modtager EAN</span>
            <Input value={receiverEan} onChange={(event) => setReceiverEan(event.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Modtager navn</span>
            <Input value={receiverName} onChange={(event) => setReceiverName(event.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Patient CPR</span>
            <Input value={patientCpr} onChange={(event) => setPatientCpr(event.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Fornavn</span>
            <Input value={patientGiven} onChange={(event) => setPatientGiven(event.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Efternavn</span>
            <Input value={patientSurname} onChange={(event) => setPatientSurname(event.target.value)} />
          </label>
          {kind && (
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Filer</span>
            <input
              type="file"
              multiple
              accept={MEDBIN_FILE_ACCEPT}
              onChange={(event) => {
                const picked = Array.from(event.target.files ?? []);
                event.target.value = '';
                let next = [...files];
                let refused: string | null = null;
                for (const pickedFile of picked) {
                  const limit = checkMedbinFile({
                    fileName: pickedFile.name,
                    byteLength: pickedFile.size,
                    existingFileCount: next.length,
                    existingTotalBytes: next.reduce((sum, item) => sum + item.size, 0),
                  });
                  if (!limit.ok) {
                    refused = limitMessage(limit.code);
                    break;
                  }
                  next.push(pickedFile);
                }
                setFiles(next);
                setError(refused);
              }}
            />
          </label>
          )}
          {files.length > 0 && (
            <ul className="space-y-1 text-sm">
              {files.map((item) => (
                <li key={`${item.name}-${item.size}-${item.lastModified}`} className="flex items-center justify-between gap-3">
                  <span>
                    {item.name} ({Math.ceil(item.size / 1024)} KB)
                  </span>
                  <button
                    type="button"
                    className="text-ds-muted"
                    onClick={() => setFiles((current) => current.filter((entry) => entry !== item))}
                  >
                    Fjern
                  </button>
                </li>
              ))}
            </ul>
          )}
          {kind ? (
            <p className="text-sm text-ds-muted">Tjekket køres på den første fil. Listen håndhæver 10 filer og 100 MB.</p>
          ) : null}
        </div>
        {error ? (
          <div className="mt-s4">
            <Banner tone="error">{error}</Banner>
          </div>
        ) : null}
        </Card>

        <Card>
          <h2 className={cn(TYPE.heading, 'text-ds-text')}>{t('medcomSection.test_inbox')}</h2>
          <p className={cn(TYPE.body, 'mt-s1 text-ds-muted')}>
            Indlæs forældrebrevet først, derefter bilaget. Intet sendes.
          </p>
          <label className={cn(TYPE.label, 'mt-s4 block text-ds-text')}>
            <span className="mb-s1 block">Indlæs XML</span>
            <input
              type="file"
              accept=".xml,text/xml"
              onChange={(event) => {
                const picked = event.target.files?.[0];
                event.target.value = '';
                if (!picked) return;
                void picked.text().then((xml) => {
                  setInbox((current) => [...current, classifyImport(picked.name, xml, knownParents)]);
                });
              }}
            />
          </label>
          <label className={cn(TYPE.label, 'mt-s4 block text-ds-text')}>
            <span className="mb-s1 block">Indlæs kvittering fra fil</span>
            <input
              type="file"
              onChange={(event) => {
                const picked = event.target.files?.[0];
                event.target.value = '';
                if (!picked) return;
                setInbox((current) => [
                  ...current,
                  {
                    fileName: picked.name,
                    status: 'kvittering',
                    detail: 'Kvitteringen er indlæst fra fil på forældrebrevet og på bilaget. Den er ikke hentet fra postkassen.',
                  },
                ]);
              }}
            />
          </label>
        </Card>

        {inbox.length === 0 ? (
          <Card>
            <EmptyState icon={<Paperclip className="h-7 w-7" />} title={t('medcomSection.empty_attachment_inbox')} />
          </Card>
        ) : (
          <Card className="p-0 nav:p-0">
            <ResponsiveTable
              columns={[
                {
                  id: 'status',
                  header: t('communicate.col_status'),
                  cell: (row) => row.status,
                },
                {
                  id: 'file',
                  header: t('communicate.attachment_label'),
                  cell: (row) => row.fileName,
                },
                {
                  id: 'detail',
                  header: t('communicate.col_ack'),
                  cell: (row) => row.detail,
                },
              ]}
              rows={inbox}
              getRowKey={(row, index) => `${row.fileName}-${index}`}
              empty={null}
            />
          </Card>
        )}

        {preview ? (
          <Card>
            <h2 className={cn(TYPE.heading, 'text-ds-text')}>
              {preview.ok ? 'Filen består tjekket' : 'Filen fejler tjekket'}
            </h2>
            <p className={cn(TYPE.body, 'mt-s1 text-ds-muted')}>Kunde: {preview.consumerName}</p>
            <div className="mt-s4 flex flex-wrap gap-s2">
              {preview.parentXml ? (
                <Button type="button" onClick={() => downloadFile('XDIS91.xml', preview.parentXml ?? '')}>
                  Hent forældrebrev
                </Button>
              ) : null}
              <Button type="button" onClick={() => downloadFile(kind === 'XBIN02' ? 'XBIN02.xml' : 'XBIN01.xml', preview.xml)}>
                Hent bilag
              </Button>
            </div>
            <ul className="mt-s3 space-y-s2 text-sm">
              {preview.checks.map((item) => (
                <li key={item.id}>
                  <span className={item.pass ? 'text-ds-green-ink' : 'text-ds-red-ink'}>
                    {item.pass ? 'Ok' : 'Fejl'}
                  </span>
                  {' — '}
                  {item.detail}
                </li>
              ))}
            </ul>
            <pre className="mt-s4 max-h-80 overflow-auto rounded-md bg-ds-toast-bg p-s4 text-xs text-ds-toast-ink">
              {preview.xml}
            </pre>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
